import { BlockType } from "../world/block-registry";
import { ItemType } from "./items";

// Slots hold block ids (0-15) or item ids (>= 100, see items.ts)
export type InventoryItem = {
  blockId: number;
  count: number;
};

export type Inventory = {
  items: (InventoryItem | null)[];
  addItem: (blockId: number, count?: number) => boolean;
  removeItem: (blockId: number, count?: number) => boolean;
  /** True if `count` of this id fits without losing anything (addItem applies partially on failure). */
  canFit: (blockId: number, count: number) => boolean;
  getCount: (blockId: number) => number;
  getTotal: () => number;
};

const MAX_STACK = 64;

/** Default 9 slots (hotbar only, Minecraft-hardcore style) — chests hold 27. */
export function createInventory(slots = 9): Inventory {
  const MAX_SLOTS = slots;
  const items: (InventoryItem | null)[] = new Array(MAX_SLOTS).fill(null);

  function addItem(blockId: number, count = 1): boolean {
    for (let i = 0; i < MAX_SLOTS; i++) {
      const slot = items[i];
      if (slot && slot.blockId === blockId && slot.count < MAX_STACK) {
        const canAdd = Math.min(count, MAX_STACK - slot.count);
        slot.count += canAdd;
        count -= canAdd;
        if (count <= 0) return true;
      }
    }

    while (count > 0) {
      const emptyIdx = items.findIndex((s) => s === null);
      if (emptyIdx === -1) return false;
      const toAdd = Math.min(count, MAX_STACK);
      items[emptyIdx] = { blockId, count: toAdd };
      count -= toAdd;
    }

    return true;
  }

  function removeItem(blockId: number, count = 1): boolean {
    let remaining = count;

    for (let i = items.length - 1; i >= 0; i--) {
      const slot = items[i];
      if (slot && slot.blockId === blockId) {
        const toRemove = Math.min(remaining, slot.count);
        slot.count -= toRemove;
        remaining -= toRemove;
        if (slot.count <= 0) items[i] = null;
        if (remaining <= 0) return true;
      }
    }

    return remaining <= 0;
  }

  function canFit(blockId: number, count: number): boolean {
    let capacity = 0;
    for (const slot of items) {
      if (slot === null) capacity += MAX_STACK;
      else if (slot.blockId === blockId) capacity += MAX_STACK - slot.count;
      if (capacity >= count) return true;
    }
    return capacity >= count;
  }

  function getCount(blockId: number): number {
    let total = 0;
    for (const slot of items) {
      if (slot && slot.blockId === blockId) total += slot.count;
    }
    return total;
  }

  function getTotal(): number {
    let total = 0;
    for (const slot of items) {
      if (slot) total += slot.count;
    }
    return total;
  }

  return { items, addItem, removeItem, canFit, getCount, getTotal };
}

export function getBlockDrop(blockId: BlockType): { blockId: number; count: number } | null {
  switch (blockId) {
    case BlockType.GRASS: return { blockId: BlockType.DIRT, count: 1 };
    case BlockType.STONE: return { blockId: BlockType.COBBLESTONE, count: 1 };
    // Ores drop items you smelt/use (Minecraft-style)
    case BlockType.COAL_ORE: return { blockId: ItemType.COAL, count: 1 };
    case BlockType.DIAMOND_ORE: return { blockId: ItemType.DIAMOND_GEM, count: 1 };
    case BlockType.LEAVES: {
      const roll = Math.random();
      if (roll < 0.1) return { blockId: BlockType.WOOD, count: 1 };
      if (roll < 0.25) return { blockId: ItemType.APPLE, count: 1 };
      // Leaves themselves drop often enough to feed the apple recipe
      if (roll < 0.6) return { blockId: BlockType.LEAVES, count: 1 };
      return null;
    }
    case BlockType.AIR:
    case BlockType.BEDROCK:
    case BlockType.WATER:
      return null;
    default:
      return { blockId, count: 1 };
  }
}
